/**
 * setupGallerySliders() — Splide carousels for the `timeline` layout's video
 * and image gallery rows (Elements/content/timeline-video-gallery.ctp,
 * content/timeline-image-gallery.ctp).
 *
 * Splide itself, not a new dependency: it is already the carousel this
 * tenant ships for the homepage hero (lib/hero.js) — @splidejs/splide/css/core
 * carries no arrow/pagination skin, exactly as hero.js's own comment explains,
 * which is why these get their own `.gallery-slider` rules in components.css
 * instead of reusing `.hero`'s (dark, photo-overlay) ones.
 *
 * No dot pagination here — a `current / total` counter instead, because nine
 * dots for nine gallery photos is a wall of identical marks; a fraction reads
 * at a glance. Splide has no built-in fraction counter, so this listens for
 * `moved` and writes the two numbers itself.
 *
 * perPage: 3 on desktop, 1 on a phone — a whole number of full slides per
 * view, no partial slide peeking at the edge. An earlier version used
 * `fixedWidth` for a half-slide peek; that was reverted (the peek was the
 * one thing the brief asked to remove), but `fixedWidth` over `perPage`
 * remains the right tool if a peek is ever wanted again.
 */
import Splide from '@splidejs/splide';
import { qs, qsa, prefersReducedMotion } from './dom.js';

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
const toPersianDigits = (n) => String(n).replace(/[0-9]/g, (d) => PERSIAN_DIGITS[d]);

export function setupGallerySliders() {
  const roots = qsa('[data-gallery-slider]');
  if (!roots.length) return;

  const reduced = prefersReducedMotion();
  const direction = document.documentElement.getAttribute('dir') === 'ltr' ? 'ltr' : 'rtl';

  roots.forEach((root) => {
    const slides = root.querySelectorAll('.splide__slide');
    const controls = qs('.gallery-slider__controls', root);

    // One slide needs no carousel — see hero.js for the same guard.
    if (slides.length < 2) {
      if (controls) controls.hidden = true;
      return;
    }

    const splide = new Splide(root, {
      perPage: 3,
      perMove: 1,
      gap: '1rem',
      rewind: true,
      direction,
      arrows: true,
      pagination: false,
      drag: true,
      speed: reduced ? 0 : 400,
      breakpoints: {
        639: { perPage: 1 },
      },
      i18n: {
        prev: 'اسلاید قبلی',
        next: 'اسلاید بعدی',
        first: 'رفتن به اسلاید اول',
        last: 'رفتن به اسلاید آخر',
        carousel: 'گالری',
        slide: 'اسلاید',
        slideLabel: 'اسلاید %s از %s',
      },
    });

    const current = controls ? qs('[data-slider-current]', controls) : null;
    const total = controls ? qs('[data-slider-total]', controls) : null;
    if (total) total.textContent = toPersianDigits(slides.length);

    splide.on('mounted moved', () => {
      if (current) current.textContent = toPersianDigits(splide.index + 1);
    });

    splide.mount();
  });
}
